import { createRootRouteWithContext, createRoute, createRouter, redirect, } from '@tanstack/react-router';
import { AppShell } from '@/components/AppShell';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { LandingPage } from '@/pages/landing/LandingPage';
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage';
import { PartiesPage } from '@/pages/parties/PartiesPage';
import { PartyDetailPage } from '@/pages/parties/PartyDetailPage';
import { WorkDetailPage } from '@/pages/works/WorkDetailPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { MePage } from '@/pages/me/MePage';
import { AccountPage } from '@/pages/account/AccountPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { AdminWorksPage } from '@/pages/admin/AdminWorksPage';
import { AdminPeoplePage } from '@/pages/admin/AdminPeoplePage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminUserDetailPage } from '@/pages/admin/AdminUserDetailPage';
import { AdminLogsPage } from '@/pages/admin/AdminLogsPage';
import { AdminLogDetailPage } from '@/pages/admin/AdminLogDetailPage';
import { AdminPersonDetailPage } from '@/pages/admin/AdminPersonDetailPage';
import { AdminToolLogsPage } from '@/pages/admin/AdminToolLogsPage';
import { AdminSettingsPage } from '@/pages/admin/AdminSettingsPage';
import { JoinPartyPage } from '@/pages/join/JoinPartyPage';
import { DiscoverPage } from '@/pages/discover/DiscoverPage';
import { AboutDataPage } from '@/pages/about/AboutDataPage';
import { PrivacyPolicyPage } from '@/pages/legal/PrivacyPolicyPage';
import { TermsOfServicePage } from '@/pages/legal/TermsOfServicePage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { SupportPage } from '@/pages/support/SupportPage';
function homeRedirect(context) {
    const id = context.auth.defaultPartyId;
    if (id)
        return redirect({ to: '/parties/$partyId', params: { partyId: id } });
    return redirect({ to: '/parties' });
}
const rootRoute = createRootRouteWithContext()({});
// --- Landing / auth page (no shell) ---
const landingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: LandingPage,
    beforeLoad: ({ context }) => {
        if (context.auth.isAuthenticated) {
            throw homeRedirect(context);
        }
    },
});
// Redirect old /login and /register paths to landing
const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    beforeLoad: ({ context }) => {
        if (context.auth.isAuthenticated) {
            throw homeRedirect(context);
        }
        throw redirect({ to: '/' });
    },
});
const registerRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/register',
    beforeLoad: ({ context }) => {
        if (context.auth.isAuthenticated) {
            throw homeRedirect(context);
        }
        throw redirect({ to: '/' });
    },
});
// --- About / data page (no shell — public) ---
const aboutDataRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/about/data',
    component: AboutDataPage,
});
// --- Legal pages (no shell — public) ---
const privacyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/legal/privacy',
    component: PrivacyPolicyPage,
});
const termsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/legal/terms',
    component: TermsOfServicePage,
});
// --- Password reset pages (no shell — public) ---
const forgotPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/forgot-password',
    component: ForgotPasswordPage,
});
const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reset-password',
    component: ResetPasswordPage,
    validateSearch: (search) => ({
        ...(typeof search.token === 'string' ? { token: search.token } : {}),
        ...(typeof search.email === 'string' ? { email: search.email } : {}),
    }),
});
// --- Support page (no shell — public) ---
const supportRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/support',
    component: SupportPage,
});
// --- Verify email page (no shell — public) ---
const verifyEmailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/verify-email',
    component: VerifyEmailPage,
    validateSearch: (search) => ({
        ...(typeof search.token === 'string' ? { token: search.token } : {}),
        ...(typeof search.email === 'string' ? { email: search.email } : {}),
    }),
});
// --- Shell layout (sidebar + bottom nav) ---
const shellRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'shell',
    component: AppShell,
});
// --- Onboarding (no shell — standalone full-screen) ---
const onboardingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/onboarding',
    component: OnboardingPage,
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (context.auth.onboardingComplete) {
            throw homeRedirect(context);
        }
    },
});
// --- Join party (standalone — no shell) ---
const joinPartyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/join/$token',
    component: () => {
        const { token } = joinPartyRoute.useParams();
        return JoinPartyPage({ token });
    },
});
// --- Auth-required routes ---
const partiesRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/parties',
    component: PartiesPage,
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
    },
});
const partyDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/parties/$partyId',
    component: PartyDetailPage,
    validateSearch: (search) => ({
        ...(typeof search.tab === 'string' ? { tab: search.tab } : {}),
    }),
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
    },
});
const workDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/works/$workId',
    component: WorkDetailPage,
    validateSearch: (search) => ({
        ...(typeof search.partyId === 'string' ? { partyId: search.partyId } : {}),
    }),
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
    },
});
const discoverRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/discover',
    component: DiscoverPage,
    validateSearch: (search) => ({
        ...(typeof search.type === 'string' ? { type: search.type } : {}),
    }),
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
    },
});
const settingsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/settings',
    component: SettingsPage,
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
    },
});
const meRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/me',
    component: MePage,
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
    },
});
const accountRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/account',
    component: AccountPage,
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
    },
});
const notificationsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/notifications',
    component: NotificationsPage,
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
    },
});
// --- Admin routes ---
const adminRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/admin',
    component: AdminLayout,
    beforeLoad: ({ context }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({ to: '/' });
        }
        if (!context.auth.onboardingComplete) {
            throw redirect({ to: '/onboarding' });
        }
        if (!context.auth.isAdmin) {
            throw homeRedirect(context);
        }
    },
});
const adminDashboardRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/',
    component: AdminDashboardPage,
});
const adminWorksRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/works',
    component: AdminWorksPage,
});
const adminPeopleRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/people',
    component: AdminPeoplePage,
});
const adminPersonDetailRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/people/$personId',
    component: AdminPersonDetailPage,
});
const adminUsersRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/users',
    component: AdminUsersPage,
});
const adminUserDetailRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/users/$userId',
    component: AdminUserDetailPage,
});
const adminLogsRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/logs',
    component: AdminLogsPage,
});
const adminLogDetailRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/logs/$logId',
    component: AdminLogDetailPage,
});
const adminToolLogsRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/tool-logs',
    component: AdminToolLogsPage,
});
const adminSettingsRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/settings',
    component: AdminSettingsPage,
});
// --- Route tree ---
const routeTree = rootRoute.addChildren([
    landingRoute,
    loginRoute,
    registerRoute,
    aboutDataRoute,
    privacyRoute,
    termsRoute,
    supportRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
    onboardingRoute,
    joinPartyRoute,
    shellRoute.addChildren([
        partiesRoute,
        partyDetailRoute,
        workDetailRoute,
        discoverRoute,
        settingsRoute,
        meRoute,
        accountRoute,
        notificationsRoute,
        adminRoute.addChildren([
            adminDashboardRoute,
            adminWorksRoute,
            adminPeopleRoute,
            adminPersonDetailRoute,
            adminUsersRoute,
            adminUserDetailRoute,
            adminLogsRoute,
            adminLogDetailRoute,
            adminToolLogsRoute,
            adminSettingsRoute,
        ]),
    ]),
]);
export const router = createRouter({
    routeTree,
    context: {
        auth: {
            isAuthenticated: false,
            isAdmin: false,
            onboardingComplete: false,
            userId: null,
            defaultPartyId: null,
        },
    },
});
